import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, Compass, Palmtree, Shirt, Sparkles } from "lucide-react";
import { useApi } from "../lib/api";
import { useSeo } from "../lib/seo";
import { DestinationSearch } from "../components/shop/DestinationSearch";
import { DestinationCard } from "../components/shop/DestinationCard";
import { ProductCard } from "../components/shop/ProductCard";
import { GridSkeleton } from "../components/shop/ProductListing";
import { SectionHead, EmptyState, Eyebrow } from "../components/shop/Common";
import { Newsletter } from "../components/shop/Newsletter";
import { SocialIcons } from "../components/layout/SocialIcons";
import { useStore } from "../context/StoreContext";
import { CATS, HERO } from "../lib/categories";

const QUICK = ["Orlando", "Miami", "Las Vegas", "Hawaii", "Cancún", "Bahamas"];

const Swoosh = () => (
  <svg viewBox="0 0 600 40" className="absolute -bottom-3 left-0 w-full sm:w-[105%] h-5 sm:h-7" preserveAspectRatio="none" aria-hidden>
    <path d="M5 30 C 150 8, 380 4, 595 14" stroke="#FFD600" strokeWidth="9" fill="none" strokeLinecap="round" strokeDasharray="900" className="animate-draw" />
  </svg>
);

const HeroImage = () => {
  const { settings } = useStore() || {};
  if (!settings) return <div className="absolute inset-0 bg-navy" />;
  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="hero-image-wrap">
      <img src={settings.hero_image || HERO} alt="Traveler on a tropical beach with palm trees and turquoise ocean" fetchPriority="high"
        className="absolute inset-0 w-full h-full object-cover object-[70%_center] animate-kenburns" data-testid="hero-image" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy/60 to-transparent" />
    </div>
  );
};

const Hero = () => (
  <section className="relative z-20 min-h-[560px] sm:min-h-[580px] lg:min-h-[62vh] flex items-center bg-navy" data-testid="hero-section">
    <HeroImage />
    <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="max-w-4xl">
        <p className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs sm:text-sm font-semibold px-4 py-1.5 animate-rise">
          <Palmtree className="w-4 h-4 text-sun" /> Destination-inspired vacation apparel
        </p>
        <h1 className="mt-6 font-brush uppercase leading-[0.85] text-shadow-hero" data-testid="hero-headline">
          <span className="block text-white text-6xl sm:text-7xl lg:text-8xl animate-rise [animation-delay:100ms]">Where are you</span>
          <span className="relative inline-block sm:whitespace-nowrap text-sun text-[4.2rem] sm:text-8xl lg:text-[8.5rem] animate-rise [animation-delay:220ms]">
            Traveling to?
            <Swoosh />
          </span>
        </h1>
        <p className="mt-8 text-white text-lg sm:text-xl max-w-xl font-medium text-shadow-hero animate-rise [animation-delay:340ms]" data-testid="hero-subtext">
          Tell us your destination and we'll show you the perfect apparel for your trip!
        </p>
        <div className="mt-8 max-w-2xl animate-rise [animation-delay:460ms] relative z-20">
          <DestinationSearch variant="hero" />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2 animate-rise [animation-delay:580ms]">
          <span className="text-white/80 text-sm font-medium mr-1">Trending:</span>
          {QUICK.map((q) => (
            <Link key={q} to={`/destinations/${q === "Cancún" ? "cancun" : q.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-full bg-white/15 hover:bg-sun hover:text-navy backdrop-blur-md border border-white/25 text-white text-sm font-semibold px-3.5 py-1.5 transition-colors" data-testid={`hero-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}>
              {q}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PopularDestinations = () => {
  const { data, loading } = useApi("/destinations", { featured: true });
  const rail = useRef(null);
  const scroll = (dir) => rail.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  useEffect(() => {
    if (rail.current) rail.current.scrollLeft = 0;
  }, [data]);
  return (
    <section className="py-16 sm:py-24 bg-white" data-testid="popular-destinations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4">
          <SectionHead eyebrow="Popular destinations" title="Pick your paradise" />
          <div className="hidden sm:flex gap-2 mb-10">
            <button onClick={() => scroll(-1)} className="w-12 h-12 rounded-full border-2 border-navy grid place-items-center hover:bg-navy hover:text-white transition-colors" aria-label="Scroll left" data-testid="destinations-scroll-left"><ArrowLeft className="w-5 h-5" /></button>
            <button onClick={() => scroll(1)} className="w-12 h-12 rounded-full bg-navy text-white grid place-items-center hover:bg-turq transition-colors" aria-label="Scroll right" data-testid="destinations-scroll-right"><ArrowRight className="w-5 h-5" /></button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div ref={rail} className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-4" data-testid="destinations-rail">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="shrink-0 w-[78vw] sm:w-80 h-[440px] rounded-[28px] bg-sand animate-pulse" />)
          : data?.map((d) => <DestinationCard key={d.slug} d={d} className="shrink-0 snap-start w-[78vw] sm:w-80 h-[440px]" />)}
        <Link key="view-all" to="/destinations" className="shrink-0 snap-start w-[60vw] sm:w-64 h-[440px] rounded-[28px] sunset-gradient grid place-items-center text-center p-6" data-testid="destinations-view-all">
          <span>
            <Compass className="w-12 h-12 text-white mx-auto" />
            <span className="block font-brush text-4xl text-white mt-3">See all destinations</span>
          </span>
        </Link>
      </div>
      </div>
    </section>
  );
};

const ProductRow = ({ params, empty, testid }) => {
  const { data, loading } = useApi("/products", params);
  if (loading) return <GridSkeleton />;
  if (!data?.length) return empty;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6" data-testid={testid}>
      {data.slice(0, 8).map((p) => <ProductCard key={p.slug} p={p} />)}
    </div>
  );
};

const FeaturedOrlando = () => (
  <section className="py-16 sm:py-24 bg-sand" data-testid="featured-orlando-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <SectionHead eyebrow="Featured destination" title={<>Featured for <span className="font-brush text-tpink text-5xl sm:text-7xl font-normal">Orlando</span></>} action="Shop Orlando" to="/destinations/orlando" />
      <ProductRow
        params={{ destination: "orlando" }}
        testid="orlando-products"
        empty={
          <EmptyState title="The Orlando drop is landing soon" text="Our original Orlando designs are being finalized. Get on the list to hear the moment they go live." testid="orlando-empty">
            <Link to="/destinations/orlando" className="rounded-full bg-navy text-white px-6 py-3 font-bold">Visit the Orlando page</Link>
          </EmptyState>
        }
      />
    </div>
  </section>
);

const Categories = () => {
  const { settings } = useStore() || {};
  return (
  <section className="py-16 sm:py-24" data-testid="categories-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <SectionHead eyebrow="Shop by category" title="Dress for the adventure" action="Shop all" to="/shop" />
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-6">
        {CATS.map(([name, to, img, color], i) => (
          <Link key={name} to={to} className={`group relative overflow-hidden rounded-[28px] ${i < 2 ? "lg:col-span-3 h-64 sm:h-80" : "lg:col-span-2 h-56 sm:h-72"} ${i === 4 ? "col-span-2 lg:col-span-2" : ""}`} data-testid={`category-tile-${name.toLowerCase()}`}>
            <img src={settings?.category_images?.[name] || img} alt={`${name} vacation apparel`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <h3 className="font-brush text-white text-4xl sm:text-5xl leading-none">{name}</h3>
              <span className={`w-11 h-11 rounded-full ${color} grid place-items-center text-navy transition-transform group-hover:-rotate-45`}><ArrowRight className="w-5 h-5" /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

const STEPS = [
  [Compass, "Choose Your Destination", "Tell us where you're headed — a city, an island, a state or a whole country.", "text-turq"],
  [Shirt, "Find Your Vacation Style", "Browse tees, hats, hoodies and tanks designed specifically for your trip.", "text-sun"],
  [Sparkles, "Wear Your Vacation Vibes", "We print your order on demand and ship it straight to your door.", "text-tpink"],
];

const HowItWorks = () => (
  <section className="relative py-20 sm:py-28 bg-navy text-white overflow-hidden grain" data-testid="how-it-works-section">
    <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-turq/20 blur-3xl" />
    <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-tpink/20 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
      <SectionHead eyebrow="How it works" title="Three steps to vacation-ready" light />
      <div className="grid md:grid-cols-3 gap-6">
        {STEPS.map(([Icon, title, text, c], i) => (
          <div key={title} className="relative rounded-[28px] bg-white/5 border border-white/10 p-8 backdrop-blur-sm hover:bg-white/10 transition-colors" data-testid={`how-step-${i + 1}`}>
            <span className={`font-brush text-8xl leading-none ${c}`}>{i + 1}</span>
            <Icon className={`absolute top-8 right-8 w-8 h-8 ${c}`} />
            <h3 className="mt-4 text-2xl font-bold">{title}</h3>
            <p className="mt-2 text-white/70">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedProducts = () => (
  <section className="py-16 sm:py-24" data-testid="featured-products-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <SectionHead eyebrow="Featured" title="Vacation favorites" action="View all" to="/shop" />
      <ProductRow params={{ featured: true }} testid="featured-products" empty={<EmptyState title="Fresh designs are on the way" text="Our first collection is being printed up. Check back soon!" testid="featured-empty" />} />
    </div>
  </section>
);

const Community = () => (
  <section className="relative py-20 sm:py-28 sunset-gradient overflow-hidden" data-testid="social-community-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Eyebrow className="!text-navy">#VacationApparelUSA</Eyebrow>
        <h2 className="mt-3 font-brush uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.9] text-white text-shadow-hero" data-testid="community-headline">Show us your vacation vibes!</h2>
        <p className="mt-6 text-navy text-lg font-semibold max-w-md">Tag <span className="bg-white/70 rounded-md px-1.5">@VacationApparelUSA</span> in your vacation photos for a chance to be featured!</p>
        <p className="mt-2 text-navy/70 text-sm max-w-md">We'll always ask for your permission before sharing any photo.</p>
        <SocialIcons className="mt-8 text-navy [&_a]:bg-white/40" />
      </div>
      <div className="grid grid-cols-3 gap-3 sm:gap-4 rotate-[-3deg]">
        {["bg-white/30", "bg-navy/80", "bg-white/30", "bg-turq/80", "bg-white/30", "bg-sun/90"].map((c, i) => (
          <div key={i} className={`aspect-square rounded-3xl ${c} backdrop-blur grid place-items-center border-2 border-white/50 ${i % 2 ? "translate-y-6" : ""}`}>
            <Camera className="w-8 h-8 text-white" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  useSeo({
    description: "Vacation Apparel: funny, sarcastic and destination-inspired vacation shirts, hats and hoodies. Tell us where you're traveling and shop apparel made for your trip.",
    jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: "Vacation Apparel", url: window.location.origin, sameAs: [] },
  });
  return (
    <>
      <Hero />
      <PopularDestinations />
      <FeaturedOrlando />
      <Categories />
      <HowItWorks />
      <FeaturedProducts />
      <Community />
      <Newsletter />
    </>
  );
}
